import React, {useState} from 'react';
import {ScrollView, StyleSheet} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';

export type GameViewerTab = {
  id: string;
  label: string;
  /** Which explorer database the game came from - shown as a small source
   * chip on the tab so masters/lichess games stay visually distinguishable
   * once more than one is open. */
  db?: 'masters' | 'lichess';
};

interface GameViewerTabsProps {
  tabs: GameViewerTab[];
  activeTabId: string | null;
  onSelectTab: (id: string) => void;
  onCloseTab: (id: string) => void;
  onCloseAll: () => void;
  /** Stepper for whichever tab is active. */
  currentPlyIndex: number;
  totalPlies: number;
  onPrev: () => void;
  onNext: () => void;
  isLoading?: boolean;
  isError?: boolean;
}

/**
 * Game-viewer command row: shown *in place of* NavArrows/OpeningChip/
 * BoardToolbarActions inside the board's existing command-bar Card when the
 * user has one or more Opening Explorer games open (see
 * `renderBoardCommandBar` in Study/index.tsx). Deliberately NOT a bar of its
 * own stacked above the board - an earlier version was, and every game
 * opened stole vertical space from the board, shrinking it (and briefly,
 * before that, getting clipped by the command bar below). Reusing the
 * command bar's own row means opening/closing games never changes the
 * board's size at all.
 *
 * Three flex children matching the row it replaces: a scrollable tab strip
 * (flex:1, so it never grows the row), a compact move stepper, and a close-
 * all button - same `justify-content: space-between` shape as
 * NavArrows/OpeningChip/BoardToolbarActions.
 */
const GameViewerTabs: React.FC<GameViewerTabsProps> = ({
  tabs,
  activeTabId,
  onSelectTab,
  onCloseTab,
  onCloseAll,
  currentPlyIndex,
  totalPlies,
  onPrev,
  onNext,
  isLoading,
  isError,
}) => {
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);
  const [closeAllHovered, setCloseAllHovered] = useState(false);
  const [prevHovered, setPrevHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);

  if (!tabs.length) {
    return null;
  }

  const prevDisabled = !!isLoading || currentPlyIndex < 0;
  const nextDisabled = !!isLoading || currentPlyIndex >= totalPlies - 1;

  return (
    <>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabScroll}
        contentContainerStyle={styles.tabScrollContent}>
        {tabs.map(tab => {
          const active = tab.id === activeTabId;
          const hovered = tab.id === hoveredTabId;
          return (
            <Box
              key={tab.id}
              testID={`game-tab-${tab.id}`}
              onPress={() => onSelectTab(tab.id)}
              onHoverIn={() => setHoveredTabId(tab.id)}
              onHoverOut={() => setHoveredTabId(null)}
              style={[
                styles.tab,
                active ? styles.tabActive : hovered && styles.tabHovered,
              ]}>
              {tab.db && (
                <Box
                  style={[
                    styles.dbChip,
                    tab.db === 'masters'
                      ? styles.dbChipMasters
                      : styles.dbChipLichess,
                  ]}>
                  <CCText
                    fontSize={7.5}
                    fontWeight="bold"
                    color={
                      tab.db === 'masters'
                        ? CCColors.Workbook.GoldDark
                        : CCColors.Workbook.Brown
                    }>
                    {tab.db === 'masters' ? 'M' : 'L'}
                  </CCText>
                </Box>
              )}
              <CCText
                fontSize={10.5}
                numberOfLines={1}
                fontWeight={active ? 'semibold' : 'medium'}
                color={
                  active ? CCColors.TextColor.Dark : CCColors.Workbook.BrownGrey
                }
                style={styles.tabLabel}>
                {tab.label}
              </CCText>
              <Box
                testID={`game-tab-close-${tab.id}`}
                onPress={() => onCloseTab(tab.id)}
                hitSlop={{top: 8, bottom: 8, left: 6, right: 6}}
                style={styles.closeBtn}>
                <Ionicons
                  name="close"
                  size={11}
                  color={
                    active
                      ? CCColors.Workbook.Brown
                      : CCColors.Workbook.BrownGrey
                  }
                />
              </Box>
            </Box>
          );
        })}
      </ScrollView>

      <Box style={styles.stepper}>
        <Box
          testID="game-tab-prev"
          onPress={onPrev}
          disabled={prevDisabled}
          onHoverIn={() => setPrevHovered(true)}
          onHoverOut={() => setPrevHovered(false)}
          style={[
            styles.stepBtn,
            prevHovered && !prevDisabled && styles.stepBtnHovered,
            prevDisabled && styles.stepBtnDisabled,
          ]}>
          <Ionicons
            name="chevron-back"
            size={14}
            color={CCColors.TextColor.Primary}
          />
        </Box>
        <CCText
          fontSize={9.5}
          fontWeight="semibold"
          color={
            isError ? CCColors.Workbook.DangerRed : CCColors.Workbook.Brown
          }
          style={styles.stepperLabel}>
          {isLoading
            ? 'Loading…'
            : isError
            ? 'Failed'
            : `${currentPlyIndex + 1}/${totalPlies}`}
        </CCText>
        <Box
          testID="game-tab-next"
          onPress={onNext}
          disabled={nextDisabled}
          onHoverIn={() => setNextHovered(true)}
          onHoverOut={() => setNextHovered(false)}
          style={[
            styles.stepBtn,
            nextHovered && !nextDisabled && styles.stepBtnHovered,
            nextDisabled && styles.stepBtnDisabled,
          ]}>
          <Ionicons
            name="chevron-forward"
            size={14}
            color={CCColors.TextColor.Primary}
          />
        </Box>
      </Box>

      <Box
        testID="game-tabs-close-all"
        onPress={onCloseAll}
        onHoverIn={() => setCloseAllHovered(true)}
        onHoverOut={() => setCloseAllHovered(false)}
        style={[styles.closeAll, closeAllHovered && styles.closeAllHovered]}>
        <Ionicons
          name="arrow-back"
          size={11}
          color={CCColors.Workbook.BrownGrey}
        />
        <CCText fontSize={9.5} color={CCColors.Workbook.BrownGrey}>
          Study
        </CCText>
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  tabScroll: {
    flex: 1,
    minWidth: 0,
  } as any,
  tabScrollContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 148,
    paddingVertical: 4,
    paddingHorizontal: 7,
    borderRadius: 7,
    marginRight: 4,
    backgroundColor: 'transparent',
    transitionProperty: 'background-color',
    transitionDuration: '0.12s',
  } as any,
  tabActive: {
    backgroundColor: CCColors.White,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.Primary.Default, 0.4),
  },
  tabHovered: {
    backgroundColor: updateColorWithOpacity(CCColors.White, 0.5),
  },
  dbChip: {
    width: 13,
    height: 13,
    borderRadius: 3.5,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 4,
  },
  dbChipMasters: {
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.Gold, 0.2),
  },
  dbChipLichess: {
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.14),
  },
  tabLabel: {
    flexShrink: 1,
    marginRight: 4,
  },
  closeBtn: {
    padding: 1,
    borderRadius: 5,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginHorizontal: 8,
    flexShrink: 0,
  },
  stepperLabel: {
    minWidth: 40,
    textAlign: 'center',
  },
  stepBtn: {
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CCColors.Primary.Light,
    transitionProperty: 'background-color',
    transitionDuration: '0.12s',
  } as any,
  stepBtnHovered: {
    backgroundColor: updateColorWithOpacity(CCColors.Primary.Default, 0.35),
  },
  stepBtnDisabled: {
    opacity: 0.4,
  },
  closeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingVertical: 4,
    paddingHorizontal: 7,
    borderRadius: 6,
    flexShrink: 0,
    backgroundColor: 'transparent',
    transitionProperty: 'background-color',
    transitionDuration: '0.12s',
  } as any,
  closeAllHovered: {
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.08),
  },
});

export default GameViewerTabs;
